// client/src/components/messages/MessageReactions.js
import React, { useState } from 'react';
import { Box, IconButton, Chip, Popover, Typography } from '@mui/material';
import AddReactionIcon from '@mui/icons-material/AddReaction';
import axiosInstance from '../../axiosInstance';

const COMMON_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👏'];

const MessageReactions = ({ messageId, reactions = [], currentUserId, onReactionChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleOpenPicker = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePicker = () => {
    setAnchorEl(null);
  };

  const handleAddReaction = async (emoji) => {
    if (loading) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/messages/${messageId}/reactions`, { emoji });
      onReactionChange(response.data);
      handleClosePicker();
    } catch (err) {
      console.error('Error adding reaction:', err);
      if (err.response?.data?.error) {
        alert(err.response.data.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveReaction = async (emoji) => {
    if (loading) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);
      onReactionChange(response.data);
    } catch (err) {
      console.error('Error removing reaction:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReactionClick = (emoji) => {
    // Check if current user already reacted with this emoji
    const userReacted = reactions.some(
      r => r.emoji === emoji && String(r.userId._id || r.userId) === String(currentUserId)
    );

    if (userReacted) {
      handleRemoveReaction(emoji);
    } else {
      handleAddReaction(emoji);
    }
  };

  // Group reactions by emoji
  const groupedReactions = reactions.reduce((acc, reaction) => {
    if (!acc[reaction.emoji]) {
      acc[reaction.emoji] = {
        count: 0,
        users: [],
        userReacted: false
      };
    }
    acc[reaction.emoji].count++;
    acc[reaction.emoji].users.push(reaction.userId?.username || 'Unknown');
    if (String(reaction.userId._id || reaction.userId) === String(currentUserId)) {
      acc[reaction.emoji].userReacted = true;
    }
    return acc;
  }, {});

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
      {Object.entries(groupedReactions).map(([emoji, data]) => (
        <Chip
          key={emoji}
          label={`${emoji} ${data.count}`}
          size="small"
          onClick={() => handleReactionClick(emoji)}
          color={data.userReacted ? 'primary' : 'default'}
          variant={data.userReacted ? 'filled' : 'outlined'}
          title={data.users.join(', ')}
          sx={{ cursor: 'pointer' }}
        />
      ))}
      
      <IconButton size="small" onClick={handleOpenPicker} disabled={loading}>
        <AddReactionIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', maxWidth: 300 }}>
          <Typography variant="caption" sx={{ width: '100%', mb: 1 }}>
            Choose a reaction:
          </Typography>
          {COMMON_EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              onClick={() => handleReactionClick(emoji)}
              sx={{ fontSize: '1.5rem' }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default MessageReactions;
